import React from 'react';
import { globalizationPath, useGlobal } from '@rcg/ui-react';
import { navigate } from '@gatsbyjs/reach-router';
import { Typography, useMediaQuery } from '@material-ui/core';
import {
  NAVIGATION_PATHS,
  FLIGHT_TYPE_CODES,
  FLIGHT_SEARCH_TYPE,
  SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY,
} from '../../utils/constants';
import useLdFlags from '../../hooks/useLdFlags';
import { getAmendAuthRedirection } from '../../utils/common';
import AirBreadcrumbs from '../AirBreadcrumbs';
import { prefixPathVar } from '../../operations/localState/reactiveVariables';
import useStyles from './useStyles';
import { getSessionStorageItem } from '../../operations/sessionStorage/utils';
import useDictionary from '../../hooks/useDictionary';
import Dictionary from '../../utils/dictionary';
import getBrand from '../../utils/getBrand';
import Theme from '../../utils/theme';

const ProgressLine = ({ selectedFlightType, extended }) => {
  const { ldFlags } = useLdFlags();

  const {
    data: { lang, market },
  } = useGlobal();
  const isCelebrityBrand = getBrand()?.id === 'cel';
  const defaultUrl = getAmendAuthRedirection(prefixPathVar, isCelebrityBrand, ldFlags);
  const getDictionaryValue = useDictionary(Dictionary);

  const classes = useStyles();

  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);

  const navigationHistoryLastEntry = getSessionStorageItem(
    SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY,
  );
  const isFromAmend =
    navigationHistoryLastEntry?.includes('/reservation') ||
    navigationHistoryLastEntry?.includes('/post-booking') ||
    navigationHistoryLastEntry?.includes('/booked');

  const isFromManage = navigationHistoryLastEntry?.includes('/manage');

  let isInteractive = true;

  const currentUrl = window.location.pathname;

  const isJustBrowsing = !!window.location.pathname.includes('/browse');

  const isMobile = useMediaQuery(Theme.breakpoints.down('xs'));

  const links = [
    {
      amend: {
        text: getDictionaryValue(Dictionary.airProgressLineCruiseReservation.key),
        url: null,
        position: 0,
      },
      bookFlights: {
        text: getDictionaryValue(Dictionary.airProgressLineBookFlight.key),
        url: `${urlPath}${NAVIGATION_PATHS.bookFlights}`,
        position: 0,
      },
      manage: {
        text: getDictionaryValue(Dictionary.airConfirmationManageFlightsCTA.key),
        url: `${urlPath}${NAVIGATION_PATHS.manage}`,
        position: 0,
      },
      page: 'backStep',
    },
    {
      text: getDictionaryValue(Dictionary.airProgressLineDepartureFlight.key),
      url: isJustBrowsing
        ? `${urlPath}${NAVIGATION_PATHS.browseDeparture}`
        : `${urlPath}${NAVIGATION_PATHS.resultsDeparture}`,
      position: 1,
      page: 'departure',
    },
    {
      text: getDictionaryValue(Dictionary.airProgressLineReturnFlight.key),
      url: isJustBrowsing
        ? `${urlPath}${NAVIGATION_PATHS.browseReturn}`
        : `${urlPath}${NAVIGATION_PATHS.resultsReturn}`,
      position: 2,
      page: 'return',
    },
    {
      text: isJustBrowsing
        ? getDictionaryValue(Dictionary.airProgressLineBrowseSummary.key)
        : getDictionaryValue(Dictionary.airProgressLineReviewFlightSummary.key),
      url: isJustBrowsing
        ? `${urlPath}${NAVIGATION_PATHS.browseSummary}`
        : `${urlPath}${NAVIGATION_PATHS.reviewFlight}`,
      position: 3,
      page: isJustBrowsing ? 'summary' : 'review-flight',
    },
    {
      text: isJustBrowsing ? null : getDictionaryValue(Dictionary.airProgressLineCheckout.key),
      url: isJustBrowsing ? null : `${urlPath}${NAVIGATION_PATHS.payment}`,
      position: isJustBrowsing ? null : 4,
      page: isJustBrowsing ? null : 'payment',
    },
  ];

  const getCurrentURL = () => {
    const segments = currentUrl.split('/');
    const lastSegment = segments[segments.length - 1];
    return lastSegment;
  };
  const handleClick = (url) => {
    if (url !== null) {
      navigate(url);
    } else {
      window.location.href = defaultUrl;
    }
  };

  const currentStepIndex = () => {
    let currentStep = 0;
    links.forEach((step) => {
      if (getCurrentURL() === step.page) {
        currentStep = step.position;
      }
    });
    return currentStep;
  };

  const getVisibleSteps = () => {
    if (isMobile) {
      return [
        links[currentStepIndex() - 1],
        links[currentStepIndex()],
        links[currentStepIndex() + 1],
      ].filter((step) => step !== undefined);
    }

    return links;
  };

  const getBackStep = (step) => {
    return (
      <Typography
        className={`${classes.textItem} ${classes.interactiveItem}`}
        key={`link-progress-line-${step.position}`}
        color="text.primary"
        tabIndex={0}
        role="button"
        onClick={() => handleClick(step.url)}>
        {step.text}
      </Typography>
    );
  };

  return (
    <AirBreadcrumbs data-testid="progressLine" className={extended ? 'extended' : ''}>
      {getVisibleSteps().map((item) => {
        if (item.page === 'backStep') {
          if (isFromAmend) {
            return getBackStep(item.amend);
          }
          if (isFromManage) {
            return getBackStep(item.manage);
          }
          return getBackStep(item.bookFlights);
        }

        if (
          (selectedFlightType === FLIGHT_TYPE_CODES.ONE_WAY &&
            item.page === FLIGHT_SEARCH_TYPE.RETURN) ||
          item.page === null
        ) {
          return null;
        }

        if (getCurrentURL() === item.page) {
          isInteractive = false;
          return (
            <Typography
              className={`${classes.textItem} ${classes.currentPage}`}
              key={`link-progress-line-${item.position}`}
              color="text.primary">
              {item.text}
            </Typography>
          );
        }

        if (isInteractive) {
          return (
            <Typography
              className={`${classes.textItem} ${classes.interactiveItem}`}
              key={`link-progress-line-${item.position}`}
              color="text.primary"
              tabIndex={0}
              role="button"
              onClick={() => handleClick(item.url)}>
              {item.text}
            </Typography>
          );
        }

        return (
          <Typography
            className={`${classes.textItem}`}
            key={`link-progress-line-${item.position}`}
            color="text.primary">
            {item.text}
          </Typography>
        );
      })}
    </AirBreadcrumbs>
  );
};

export default ProgressLine;
